import { TAEntity, cleanFormValue, useHttp, useTranslation } from '@threatadvisor/react-utils';
import { FormInstance, FormProps, Spin } from 'antd';
import { NamePath } from 'antd/es/form/interface';
import React, {
  ReactElement,
  ReactNode,
  Reducer,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';
import { Form } from './FormElements';

type RecursivePartial<T> = Parameters<FormInstance<T>['setFieldsValue']>[0];
type FieldData<T> = Parameters<FormInstance<T>['setFields']>[0][0];

export const AutosaveFormContext =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  React.createContext<AutosaveFormInstance<any> | undefined>(undefined);

type Endpoint<T> = string | ((values: Partial<T>) => string);
type AutosaveFormInstanceCallbacks<T> = {
  onError?: (err: Error) => Promise<Error> | void;
  afterCreate?: (values: T) => Promise<unknown> | void;
  afterUpdate?: (changedValues: Partial<T>, allValues: T) => void;
  setSaving?: (loading: boolean) => void;
};
type AutosaveFormPropsBase<T> = Omit<FormProps, 'form' | 'children'> &
  AutosaveFormInstanceCallbacks<T> & {
    children?: ReactNode;
    initialValues?: Partial<T>;
    delay?: number;
    loadDataOnMount?: boolean;
    autoSaveChange?: (changedValues: Partial<T>) => boolean;
    useFullModel?: boolean;
  };
export type AutosaveFormPropsWithForm<T> = AutosaveFormPropsBase<T> & {
  form: AutosaveFormInstance<T>;
};
export type AutosaveFormPropsWithEndpoint<T> = AutosaveFormPropsBase<T> & {
  endpoint: Endpoint<T>;
};
export type AutosaveFormProps<T> = AutosaveFormPropsWithForm<T> | AutosaveFormPropsWithEndpoint<T>;

/*
 * `AutosaveForm` and `AutosaveFormInstance` are almost a drop-in replacement for antd `Form` and `FormInstance`, with a few differences:
 * 1. You have to provide the API endpoint either as a prop, or by constructing the AutosaveFormInstance and then passing that in the `form` prop
 * 2. The AutosaveFormInstance has `updateFields` and `updateFieldsValue` functions, which are the auto-save equivalent of `setFields` and `setFieldsValue` (which modify the form without saving)
 * 3. The form will not auto-save unless there is a `uid` property in the `initialValues`, or the form is submitted and the POST request returns an entity with a `uid` property
 *
 * Other optional props are:
 *  - `afterCreate`: Callback that will contain the entity returned from the POST request after it is created
 *  - `afterUpdate`: Callback that will contain the changed and full values after each time a PATCH update request is made
 *  - `delay`: The amount of time in milliseconds to debounce changes before saving a field (default 1000ms)
 *  - `autoSaveChange`: A function that takes a partial object of what will be saved, and returns whether or not the form should proceed with automatically saving
 *  - `setSaving`: A function that will be called with a boolean value when network requests start and finish
 *  - `loadDataOnMount`: Boolean value specifying whether or not the form should load the full entity from the database on mount (useful if your initialValues is only a partial entity or just the uid)
 *  - `useFullModel`: Boolean value to tell if we should put the full model back to the controller.
 */
function AutosaveForm<T extends TAEntity>(props: AutosaveFormProps<T>): ReactElement {
  const {
    initialValues,
    loadDataOnMount,
    onError,
    afterCreate,
    afterUpdate,
    setSaving,
    delay,
    onValuesChange,
    onFinish,
    autoSaveChange,
    useFullModel,
    ...formProps
  } = props;
  const [form] = AutosaveForm.useForm<T>('form' in props ? props.form : props.endpoint, undefined);
  const timeouts = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (initialValues?.uid && loadDataOnMount) {
      setLoading(true);
      form.load(initialValues.uid, true).finally(() => setLoading(false));
    } else if (initialValues?.uid) {
      form.load(initialValues.uid);
    }
    // Only want to run this on mount, ignore later changes
  }, []);

  useEffect(() => {
    form.setCallbacks({ onError, afterUpdate, afterCreate, setSaving });
  }, [onError, afterUpdate, afterCreate, setSaving, form]);

  const handleValuesChange = useCallback(
    async (changedValues: Partial<T>) => {
      for (const fieldName in changedValues) {
        try {
          await form.validateFields([fieldName]);

          if (fieldName !== 'uid') {
            if (timeouts.current[fieldName]) clearTimeout(timeouts.current[fieldName]);
            // use allValue[fieldName] to handle nested fields like references
            timeouts.current[fieldName] = setTimeout(() => {
              const allValues = form.getFieldsValue(true); // Get latest values

              // override to use the full model, rather than individual fields
              if (useFullModel) {
                form.updateFields(allValues, false);
              } else {
                form
                  // Use allValues so that on nested properties, we get the full object to send
                  .updateFieldValue(
                    fieldName,
                    cleanFormValue(allValues[fieldName] ?? changedValues[fieldName])
                  )
                  .catch(e => {
                    onError && onError(e);
                  })
                  .finally(() => {
                    delete timeouts.current[fieldName];
                  });
              }
            }, delay ?? 1000);
          }
        } catch (e) {
          if (e instanceof Error) onError && onError(e as Error);
        }
      }
    },
    [timeouts, form, delay]
  );

  const formComponent = (
    <AutosaveFormContext.Provider value={form}>
      <Form<T>
        onFinish={async (values: any) => {
          // Only one of these will actually do anything, depending if the uid is set
          await form.update();
          await form.create();
          onFinish && onFinish(values);
        }}
        onValuesChange={(changedValues: Partial<T>, allValues: any) => {
          setTimeout(() => {
            if (!autoSaveChange || autoSaveChange(changedValues)) handleValuesChange(changedValues);
          }, 0);
          onValuesChange && onValuesChange(changedValues, allValues);
        }}
        {...formProps}
        initialValues={initialValues}
        form={form}
      />
    </AutosaveFormContext.Provider>
  );
  if (loadDataOnMount) {
    return <Spin spinning={loading}>{formComponent}</Spin>;
  }
  return formComponent;
}

export interface AutosaveFormInstance<T> extends FormInstance<T> {
  updateFieldValue: (fieldName: string, value: unknown) => Promise<void>;
  updateFieldsValue: (values: RecursivePartial<T>, skipValidation?: boolean) => void;
  updateFields: (fields: FieldData<T>[], skipValidation?: boolean) => void;
  create: (validate?: boolean) => Promise<T>;
  update: () => Promise<T>;
  load: (uid: string, fromNetwork?: boolean) => Promise<void>;
  getEndpoint: (values: T) => string;
  setCallbacks: (callbacks: AutosaveFormInstanceCallbacks<T>) => void;
}

AutosaveForm.useForm = function <T extends TAEntity>(
  // If endpointOrForm is a function, it should be pure and not change
  // If endpointOrForm is a form instance, it should never change anyways
  endpointOrForm: Endpoint<T> | AutosaveFormInstance<T>,
  formInstance?: FormInstance<T>
): [AutosaveFormInstance<T>] {
  const [form] = Form.useForm<T>(
    typeof endpointOrForm === 'object' ? (endpointOrForm as unknown as FormInstance) : formInstance
  );

  const uidRef = useRef<string | null>(null);
  const { t } = useTranslation();
  const callbacksRef = useRef<AutosaveFormInstanceCallbacks<T> | null>(null);
  const http = useHttp();

  const getEndpoint = useCallback(
    (values: T): string => {
      return typeof endpointOrForm === 'string'
        ? endpointOrForm
        : typeof endpointOrForm === 'function'
          ? endpointOrForm(values)
          : endpointOrForm.getEndpoint(values);
    },

    []
  );

  // use inflight count to handle case where multiple save requests are active at once
  const [inflightCount, setSaving] = useReducer<Reducer<number, boolean>>(
    (state: number, action: any) => (action ? state + 1 : state - 1),
    0
  );
  useEffect(() => {
    callbacksRef.current?.setSaving && callbacksRef.current.setSaving(inflightCount > 0);
  }, [inflightCount, callbacksRef]);

  const updateFieldValue = useCallback(
    async (fieldName: string, value: unknown, skipValidation = false) => {
      if (uidRef.current) {
        // First, make sure field(s) are valid
        if (!skipValidation) {
          try {
            const fieldsToValidate: (string | number | (string | number)[])[] = [];
            if (Array.isArray(value)) {
              // Validate nested fields in a Form.List
              value.forEach((v, i) => {
                if (typeof v === 'object') {
                  Object.keys(v).forEach(key => {
                    fieldsToValidate.push([fieldName, i, key]);
                  });
                } else {
                  fieldsToValidate.push([fieldName, i]);
                }
              });
            } else {
              fieldsToValidate.push(fieldName);
            }

            await form.validateFields(fieldsToValidate);
          } catch (err) {
            console.warn(err);
            err instanceof Error &&
              callbacksRef.current?.onError &&
              callbacksRef.current.onError(err);
            return;
          }
        }

        // Then, try to save the field
        try {
          // sets the global saving value
          setSaving(true);

          // only use UID once in string; something going on here that im not following
          const endpointString = getEndpoint(form.getFieldsValue(true));

          const uidExistsInEndpointUrl = endpointString.indexOf(uidRef.current);
          const endpointUrl =
            uidExistsInEndpointUrl === -1
              ? `${getEndpoint(form.getFieldsValue(true))}/${uidRef.current}`
              : endpointString;

          // updates specific field
          const returnVal = await http.updateField(endpointUrl, fieldName, value);

          // adds the data back into the form fields values
          // DO NOT DO THIS form.setFieldValue(fieldName, returnVal);
          // If you want it back in the form.. .you have it in the afterUpdate callback
          callbacksRef.current?.afterUpdate &&
            callbacksRef.current.afterUpdate(
              { [fieldName]: returnVal } as Partial<T>,
              form.getFieldsValue(true)
            );
        } catch (err) {
          err instanceof Error &&
            callbacksRef.current?.onError &&
            callbacksRef.current.onError(err);
        } finally {
          setSaving(false);
        }
      }
    },

    []
  );

  const updateFields = useCallback(
    async (fields: T, skipValidation = false) => {
      if (uidRef.current) {
        // TODO: First, make sure field(s) are valid

        // Then, try to save the field
        try {
          // sets the global saving value
          setSaving(true);

          // updates specific field
          await http.update(`${getEndpoint(form.getFieldsValue(true))}`, fields);

          // adds the data back into the form fields values
          callbacksRef.current?.afterUpdate &&
            callbacksRef.current.afterUpdate({ ...fields }, form.getFieldsValue(true));
        } catch (err) {
          console.error(err);
          err instanceof Error &&
            callbacksRef.current?.onError &&
            callbacksRef.current.onError(err);

          throw err;
        } finally {
          setSaving(false);
        }
      }
    },

    []
  );

  const mutexRef = useRef<boolean>(false);
  const wrapForm: AutosaveFormInstance<T> = useMemo(
    () =>
      typeof endpointOrForm === 'object'
        ? endpointOrForm
        : {
            ...form,
            resetFields: (fields?: NamePath[]) => {
              form.resetFields(fields);
              uidRef.current = null;
            },
            updateFieldValue,
            load: async (uid: string, fromNetwork?: boolean) => {
              if (fromNetwork) {
                try {
                  setSaving(true);
                  // only use UID once in string; something going on here that im not following
                  const endpointString = getEndpoint(form.getFieldsValue(true));

                  const uidExistsInEndpointUrl = endpointString.indexOf(uid);
                  const endpointUrl =
                    uidExistsInEndpointUrl === -1
                      ? `${getEndpoint(form.getFieldsValue(true))}/${uid}`
                      : endpointString;

                  const result = await http.get<T>(endpointUrl, undefined);
                  uidRef.current = result.uid ?? '';
                  if (result) {
                    form.setFieldsValue(result as unknown as RecursivePartial<T>);
                  }
                } catch (err) {
                  err instanceof Error &&
                    callbacksRef.current?.onError &&
                    callbacksRef.current.onError({
                      ...err,
                      message: t('errors.autosaveLoadFailed', 'Load failed')
                    });
                  throw err;
                } finally {
                  setSaving(false);
                }
              } else {
                uidRef.current = uid;
              }
            },
            create: async (validate = true) => {
              if (validate) await form.validateFields();
              const values = form.getFieldsValue(true);
              const uid = uidRef.current ?? values.uid;

              if (!uid && mutexRef.current === false) {
                try {
                  setSaving(true);
                  mutexRef.current = true;
                  const result = await http.create<T>(
                    getEndpoint(values) + (uid ? '/' + uid : ''),
                    values
                  );
                  uidRef.current = result.uid ?? '';
                  callbacksRef.current?.afterCreate && callbacksRef.current?.afterCreate(result);
                  return result;
                } catch (err) {
                  mutexRef.current = false;
                  err instanceof Error &&
                    callbacksRef.current?.onError &&
                    callbacksRef.current.onError({
                      ...err,
                      message: t('errors.autosaveCreateFailed', 'Save failed')
                    });

                  throw err;
                } finally {
                  mutexRef.current = false;
                  setSaving(false);
                }
              }
              return values;
            },
            update: async () => {
              try {
                await form.validateFields();
              } catch {
                return;
              }
              const values = form.getFieldsValue(true);
              const uid = uidRef.current ?? values.uid;
              if (uid && mutexRef.current === false) {
                try {
                  setSaving(true);
                  mutexRef.current = true;
                  const updateResponse = await http.update<T>(
                    getEndpoint(values) + (uid ? '/' + uid : ''),
                    values
                  );
                  const updatedValues = { ...values, ...updateResponse };
                  callbacksRef.current?.afterUpdate &&
                    callbacksRef.current.afterUpdate(updatedValues, values);
                  return updatedValues;
                } catch (err) {
                  err instanceof Error &&
                    callbacksRef.current?.onError &&
                    callbacksRef.current.onError({
                      ...err,
                      message: t('errors.autosaveUpdateFailed', 'Update failed')
                    });

                  throw err;
                } finally {
                  setSaving(false);
                  mutexRef.current = false;
                }
              }
              return values;
            },
            updateFieldsValue: (values: RecursivePartial<T>, skipValidation?: boolean) => {
              form.setFieldsValue(values);
              const allValues = form.getFieldsValue(true);
              Object.keys(values).forEach(key => {
                updateFieldValue(key, allValues[key], skipValidation);
              });
            },
            updateFields: (fields: FieldData<T>[], skipValidation?: boolean) => {
              const allValues = form.getFieldsValue(true);
              updateFields(allValues, skipValidation);
            },
            getEndpoint,
            setCallbacks: (callbacks: AutosaveFormInstanceCallbacks<T>) =>
              (callbacksRef.current = callbacks)
          },
    // Make sure form is always the same

    []
  );

  return [wrapForm];
};

export default AutosaveForm;
